import React, { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import {
  ArrowClockwise, MagnifyingGlass, Phone, Prohibit, ArrowUUpLeft,
  ShieldCheck, ShieldWarning, Check, ChatCircleText, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * AdminSmsSuppressionsPage — TCPA audit + restore console for the
 * `sms_suppression` collection.
 *
 * Rows come from `GET /admin/sms-suppressions`. Rows with
 * `status === "stopped"` show a "Opt back in" button that posts to
 * `POST /admin/sms-suppressions/{phone}/restore`. Restored rows keep
 * their history — the row flips to green but the STOP/START counts
 * and audit chain remain visible.
 *
 * Bulk mode: checkbox column lets admins pick several stopped rows
 * and restore them in one call via
 * `POST /admin/sms-suppressions/bulk-restore`.
 */
export default function AdminSmsSuppressionsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeStops, setActiveStops] = useState(0);
  const [restoringPhone, setRestoringPhone] = useState(null);
  const [selectedPhones, setSelectedPhones] = useState(new Set());
  const [bulkRestoring, setBulkRestoring] = useState(false);
  const [bulkNoteOpen, setBulkNoteOpen] = useState(false);
  const [bulkNote, setBulkNote] = useState("");
  // Filter toggle — when ON, only rows with ≥1 `restore_history[].note`
  // entry are visible. Lets ops jump straight to the audited restores.
  const [notesOnlyFilter, setNotesOnlyFilter] = useState(false);
  // Undo window preference — 5s (default) or 10s. Applies to the
  // Re-stop undo toast. Persisted so admins pick their own comfort
  // level once and don't retoggle every session.
  const [undoWindowSec, setUndoWindowSec] = useState(() => {
    try {
      const raw = localStorage.getItem("adminSmsSuppressionsUndoWindowSec");
      const n = parseInt(raw, 10);
      return n === 10 ? 10 : 5;
    } catch {
      return 5;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "adminSmsSuppressionsUndoWindowSec", String(undoWindowSec),
      );
    } catch {
      // Silent — value persists in memory this session.
    }
  }, [undoWindowSec]);
  // Note-history drawer — tracks which phone rows are currently
  // expanded so the drawer state survives filter changes. Set for
  // O(1) toggle; the render side reads `.has(phone)`.
  const [expandedPhones, setExpandedPhones] = useState(() => new Set());
  const toggleExpanded = (phone) => {
    setExpandedPhones((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  };
  // Multi-level undo stacks:
  //   `undoStack`: ops that CAN be undone next Cmd+Z press.
  //     Each entry: { direction: 'restore' | 'stop', phones: [...] }.
  //   `redoStack`: ops that were just undone; Cmd+Shift+Z redoes them.
  //   Both cap at 10 entries to avoid unbounded memory growth.
  //   Persisted to sessionStorage so a page refresh doesn't wipe an
  //   in-flight audit window (survives F5 but not tab-close).
  const SESSION_KEY = "adminSmsSuppressionsUndoState";
  const _hydrate = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return { undo: [], redo: [] };
      const parsed = JSON.parse(raw);
      return {
        undo: Array.isArray(parsed?.undo) ? parsed.undo : [],
        redo: Array.isArray(parsed?.redo) ? parsed.redo : [],
      };
    } catch {
      return { undo: [], redo: [] };
    }
  };
  const _initial = _hydrate();
  const undoStackRef = useRef(_initial.undo);
  const redoStackRef = useRef(_initial.redo);
  const HISTORY_CAP = 10;
  // Mirrored counts so the compact history chip updates reactively.
  // Refs alone don't trigger rerenders; state does.
  const [historyDepth, setHistoryDepth] = useState({
    undo: _initial.undo.length, redo: _initial.redo.length,
  });
  const _syncDepth = () => {
    setHistoryDepth({
      undo: undoStackRef.current.length,
      redo: redoStackRef.current.length,
    });
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        undo: undoStackRef.current,
        redo: redoStackRef.current,
      }));
    } catch {
      // sessionStorage disabled/full — degrade silently, in-memory
      // stacks still work for the current page life.
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/sms-suppressions");
      setRows(data.suppressions || []);
      setActiveStops(data.active_stops || 0);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to load SMS suppressions",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const restore = async (phone) => {
    setRestoringPhone(phone);
    try {
      const { data } = await client.post(
        `/admin/sms-suppressions/${encodeURIComponent(phone)}/restore`,
      );
      if (data.already_active) {
        toast.info(`${phone} was already opted in.`);
      } else {
        toast.success(`${phone} is now opted back in.`);
      }
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to restore ${phone}`,
      );
    } finally {
      setRestoringPhone(null);
    }
  };

  // Re-stop a single phone from the drawer — "walk back a mistaken
  // restore" without leaving the row. Uses the existing bulk-stop
  // endpoint with a single-phone payload so we don't have to build
  // a new endpoint. Refreshes the list to flip the row's status
  // chip back to `stopped`.
  //
  // Also surfaces a Sonner toast with an `Undo (5s)` action so ops
  // can walk BACK the walk-back — fires bulk-restore w/ a
  // `Walk-back undone` note stamped on the audit trail so the
  // history drawer records the reversal.
  const [restoppingPhone, setRestoppingPhone] = useState(null);
  const restopPhone = async (phone) => {
    setRestoppingPhone(phone);
    try {
      await client.post(
        "/admin/sms-suppressions/bulk-stop",
        { phones: [phone] },
      );
      await load();
      toast.success(
        `${phone} re-stopped — no more SMS will be sent.`,
        {
          duration: undoWindowSec * 1000,
          action: {
            label: `Undo (${undoWindowSec}s)`,
            onClick: async () => {
              try {
                await client.post(
                  "/admin/sms-suppressions/bulk-restore",
                  { phones: [phone], note: "Walk-back undone" },
                );
                toast.success(`${phone} restored — walk-back reversed.`);
                await load();
              } catch (e) {
                toast.error(
                  e?.response?.data?.detail
                    || `Failed to undo re-stop for ${phone}`,
                );
              }
            },
          },
        },
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to re-stop ${phone}`,
      );
    } finally {
      setRestoppingPhone(null);
    }
  };

  const togglePhone = (phone) => {
    setSelectedPhones((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  };

  const stoppedPhones = useMemo(
    () => rows.filter((r) => r.status === "stopped").map((r) => r.phone),
    [rows],
  );

  const allStoppedSelected = stoppedPhones.length > 0
    && stoppedPhones.every((p) => selectedPhones.has(p));

  const toggleSelectAllStopped = () => {
    if (allStoppedSelected) {
      setSelectedPhones(new Set());
    } else {
      setSelectedPhones(new Set(stoppedPhones));
    }
  };

  const _pushUndo = (op) => {
    undoStackRef.current.push(op);
    if (undoStackRef.current.length > HISTORY_CAP) {
      undoStackRef.current.shift();
    }
    // Any new user action clears the redo stack — matches standard
    // undo/redo semantics (browser, IDE, etc.)
    redoStackRef.current = [];
    _syncDepth();
  };

  const bulkRestore = async () => {
    const phones = Array.from(selectedPhones);
    if (!phones.length) return;
    // Open the note prompt on the FIRST click so ops can attach a
    // short justification. Empty note is fine — the field is optional.
    if (!bulkNoteOpen) {
      setBulkNoteOpen(true);
      return;
    }
    setBulkRestoring(true);
    try {
      const { data } = await client.post(
        "/admin/sms-suppressions/bulk-restore",
        { phones, note: bulkNote.trim() || undefined },
      );
      const parts = [];
      if (data.restored) parts.push(`${data.restored} restored`);
      if (data.already_active) parts.push(`${data.already_active} were active`);
      if (data.not_found) parts.push(`${data.not_found} not found`);

      const flippedPhones = (data.results || [])
        .filter((r) => r.status === "active" && r.already_active === false)
        .map((r) => r.phone);

      if (flippedPhones.length > 0) {
        _pushUndo({ direction: "restore", phones: flippedPhones });
        toast.success(parts.join(" · ") || "Bulk restore complete", {
          duration: 60_000,
          description: `Cmd+Z to undo · Cmd+Shift+Z to redo · history: ${undoStackRef.current.length}`,
          action: {
            label: "Undo",
            onClick: () => triggerUndo(),
          },
        });
      } else {
        toast.success(parts.join(" · ") || "Bulk restore complete");
      }

      setSelectedPhones(new Set());
      setBulkNoteOpen(false);
      setBulkNote("");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Bulk restore failed",
      );
    } finally {
      setBulkRestoring(false);
    }
  };

  // Reverse a bulk-restore or bulk-stop op depending on the direction.
  // Returns the API result so callers can push the reverse op onto the
  // opposite stack for redo semantics.
  const _reverseOp = async (op) => {
    const url = op.direction === "restore"
      ? "/admin/sms-suppressions/bulk-stop"
      : "/admin/sms-suppressions/bulk-restore";
    const { data } = await client.post(url, { phones: op.phones });
    return data;
  };

  // Cmd+Z: pop the last op off the undo stack, reverse it, push the
  // reverse op onto the redo stack.
  const triggerUndo = async () => {
    const op = undoStackRef.current.pop();
    if (!op) {
      toast.info("Nothing left to undo.");
      return;
    }
    try {
      await _reverseOp(op);
      redoStackRef.current.push(op);
      if (redoStackRef.current.length > HISTORY_CAP) {
        redoStackRef.current.shift();
      }
      _syncDepth();
      const flipped = op.phones.length;
      toast.info(
        `Undo · ${flipped} phone${flipped !== 1 ? "s" : ""} re-${op.direction === "restore" ? "suppressed" : "activated"}`,
      );
      await load();
    } catch (e) {
      // Restore the op so the user can retry
      undoStackRef.current.push(op);
      _syncDepth();
      toast.error(
        e?.response?.data?.detail || "Undo failed. Retry.",
      );
    }
  };

  // Cmd+Shift+Z: pop the last op off the redo stack, re-apply it,
  // push it back onto the undo stack so a subsequent Cmd+Z reverses again.
  const triggerRedo = async () => {
    const op = redoStackRef.current.pop();
    if (!op) {
      toast.info("Nothing to redo.");
      return;
    }
    try {
      await _reverseOp({
        direction: op.direction === "restore" ? "stop" : "restore",
        phones: op.phones,
      });
      undoStackRef.current.push(op);
      if (undoStackRef.current.length > HISTORY_CAP) {
        undoStackRef.current.shift();
      }
      _syncDepth();
      const flipped = op.phones.length;
      toast.info(
        `Redo · ${flipped} phone${flipped !== 1 ? "s" : ""} re-${op.direction === "restore" ? "activated" : "suppressed"}`,
      );
      await load();
    } catch (e) {
      redoStackRef.current.push(op);
      _syncDepth();
      toast.error(
        e?.response?.data?.detail || "Redo failed. Retry.",
      );
    }
  };

  // Global hotkeys: Cmd/Ctrl+Z = undo · Cmd/Ctrl+Shift+Z = redo.
  useEffect(() => {
    const handler = (e) => {
      const zKey = e.key.toLowerCase() === "z";
      if (!zKey) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      // Don't hijack undo inside form controls
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      e.preventDefault();
      toast.dismiss();
      if (e.shiftKey) triggerRedo();
      else triggerUndo();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      // Note-only gate: skip rows that have no notes in history.
      if (notesOnlyFilter) {
        const hasAnyNote = (r.restore_history || []).some(
          (h) => h && (h.note || "").trim(),
        );
        if (!hasAnyNote) return false;
      }
      if (!q) return true;
      // Search matches phone, status, OR any note text in the row's
      // restore history. Ops often remembers "the one about the typo"
      // more than the phone number.
      const noteMatch = (r.restore_history || []).some(
        (h) => (h?.note || "").toLowerCase().includes(q),
      );
      return (r.phone || "").toLowerCase().includes(q)
        || (r.status || "").toLowerCase().includes(q)
        || noteMatch;
    });
  }, [rows, query, notesOnlyFilter]);

  // Count of rows that have ≥1 note — surfaced in the toggle label so
  // ops sees at a glance how many audited restores exist.
  const notedRowsCount = useMemo(
    () => rows.filter((r) => (r.restore_history || []).some(
      (h) => h && (h.note || "").trim(),
    )).length,
    [rows],
  );

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8"
      data-testid="admin-sms-suppressions-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="pill pill-info mb-2 inline-flex items-center gap-1.5">
            <Prohibit size={11} weight="fill" /> ADMIN · SMS SUPPRESSIONS
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            SMS Opt-Out Registry
          </h1>
          <p className="text-neutral-400 text-sm mt-2 max-w-2xl">
            Every phone that texted STOP/UNSUBSCRIBE. Use "Opt back in"
            when a customer calls to reverse an opt-out. TCPA compliance
            audit trail is preserved on every row.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {(historyDepth.undo > 0 || historyDepth.redo > 0) && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-3 py-2 bg-white/[0.04] border border-white/10 text-neutral-400"
              data-testid="admin-sms-suppressions-history-chip"
              title="Cmd+Z to undo · Cmd+Shift+Z to redo"
            >
              <span className={historyDepth.undo > 0 ? "text-white font-mono" : "font-mono"}>
                {historyDepth.undo}
              </span>{" "}
              undo ·{" "}
              <span className={historyDepth.redo > 0 ? "text-white font-mono" : "font-mono"}>
                {historyDepth.redo}
              </span>{" "}
              redo
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-sms-suppressions-refresh"
            className="btn-secondary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
          >
            <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Active stops</div>
          <div
            className="text-2xl font-mono font-bold text-amber-300 mt-1"
            data-testid="admin-sms-suppressions-active-count"
          >
            {activeStops}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total on record</div>
          <div className="text-2xl font-mono font-bold text-white mt-1">
            {rows.length}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Restored / opted back in</div>
          <div className="text-2xl font-mono font-bold text-emerald-300 mt-1">
            {rows.length - activeStops}
          </div>
        </div>
      </div>

      {/* Search + notes-only filter */}
      <div className="mb-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search phone or note text…"
            data-testid="admin-sms-suppressions-search"
            className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#FF5A1F]/40"
          />
        </div>
        <button
          type="button"
          onClick={() => setNotesOnlyFilter((v) => !v)}
          data-testid="admin-sms-suppressions-notes-only-toggle"
          disabled={notedRowsCount === 0}
          aria-pressed={notesOnlyFilter}
          className={`inline-flex items-center gap-1.5 text-[11px] rounded-full px-4 py-2 border transition-colors shrink-0 ${
            notesOnlyFilter
              ? "bg-sky-500/20 border-sky-500/40 text-sky-200 font-semibold"
              : "border-white/10 text-neutral-400 hover:text-white hover:border-white/30"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
          title={
            notedRowsCount > 0
              ? "Show only rows that have an admin restore note"
              : "No noted rows to filter — bulk-restore with a note to see this filter light up"
          }
        >
          <ChatCircleText size={12} weight="fill" />
          Notes only
          <span className="tabular-nums font-mono text-[10px] opacity-70">
            {notedRowsCount}
          </span>
        </button>
        {/* Undo window preference — controls how long the Re-stop
            undo toast stays open. Small segmented pill so admins can
            switch between fast (5s) and forgiving (10s) modes. */}
        <div
          className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.02] p-0.5 shrink-0"
          data-testid="admin-sms-suppressions-undo-window-picker"
          title="How long the Re-stop undo toast stays open"
        >
          <span className="text-[9px] uppercase tracking-widest text-neutral-500 pl-2 pr-1">
            Undo
          </span>
          {[5, 10].map((sec) => (
            <button
              key={sec}
              type="button"
              onClick={() => setUndoWindowSec(sec)}
              data-testid={`admin-sms-suppressions-undo-window-${sec}s`}
              aria-pressed={undoWindowSec === sec}
              className={`text-[10px] font-mono tabular-nums h-6 px-2 rounded-full transition-colors ${
                undoWindowSec === sec
                  ? "bg-[#FF5A1F] text-white font-bold"
                  : "text-neutral-500 hover:text-white"
              }`}
            >
              {sec}s
            </button>
          ))}
        </div>
      </div>

      {loading && rows.length === 0 && (
        <div className="card-surface p-8 text-center text-neutral-400">
          Loading suppressions…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div
          className="card-surface p-8 text-center"
          data-testid="admin-sms-suppressions-empty"
        >
          <ShieldCheck
            size={32}
            weight="duotone"
            className="text-emerald-400 mx-auto mb-3"
          />
          <div className="mb-2 text-white text-sm font-semibold">
            No SMS suppressions on record
          </div>
          <div className="text-xs text-neutral-500">
            When someone texts STOP to a FreightPilot number, they'll appear here.
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          {/* Bulk action bar — appears when >=1 phone is checked */}
          {selectedPhones.size > 0 && (
            <div
              className="mb-3 rounded-lg border border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.08] px-4 py-3 flex flex-col gap-3"
              data-testid="admin-sms-suppressions-bulk-bar"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                <div className="text-sm text-white">
                  <b>{selectedPhones.size}</b> phone{selectedPhones.size !== 1 ? "s" : ""} selected
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedPhones(new Set());
                      setBulkNoteOpen(false);
                      setBulkNote("");
                    }}
                    data-testid="admin-sms-suppressions-bulk-clear"
                    className="text-[11px] rounded-full px-3 py-1.5 border border-white/20 text-neutral-300 hover:text-white hover:border-white/40 transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={bulkRestore}
                    disabled={bulkRestoring}
                    data-testid="admin-sms-suppressions-bulk-restore"
                    className="text-[11px] rounded-full px-4 py-1.5 bg-[#FF5A1F] hover:bg-[#FF5A1F]/90 disabled:opacity-50 text-white font-semibold inline-flex items-center gap-1.5"
                  >
                    <ArrowUUpLeft size={11} weight="bold" />
                    {bulkRestoring
                      ? "Restoring…"
                      : bulkNoteOpen
                        ? `Confirm restore ${selectedPhones.size}`
                        : `Opt ${selectedPhones.size} back in`}
                  </button>
                </div>
              </div>
              {bulkNoteOpen && (
                <div
                  className="flex flex-col sm:flex-row gap-2"
                  data-testid="admin-sms-suppressions-bulk-note-form"
                >
                  <input
                    type="text"
                    autoFocus
                    value={bulkNote}
                    onChange={(e) => setBulkNote(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") bulkRestore(); }}
                    placeholder="Optional note — why is this batch being restored? (audit trail)"
                    maxLength={200}
                    data-testid="admin-sms-suppressions-bulk-note-input"
                    className="flex-1 bg-white/[0.04] border border-white/15 rounded-full px-4 py-1.5 text-xs text-white placeholder:text-neutral-500 focus:outline-none focus:border-white/40"
                  />
                  <span className="text-[10px] text-neutral-500 self-center">
                    {bulkNote.length}/200 · Enter to confirm · empty is fine
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="card-surface overflow-hidden" data-testid="admin-sms-suppressions-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02]">
                <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="w-10 px-3 py-3">
                    {stoppedPhones.length > 0 && (
                      <button
                        onClick={toggleSelectAllStopped}
                        data-testid="admin-sms-suppressions-select-all"
                        className={`w-4 h-4 rounded border-2 inline-flex items-center justify-center transition-colors ${
                          allStoppedSelected
                            ? "bg-[#FF5A1F] border-[#FF5A1F]"
                            : "border-white/30 hover:border-white/60"
                        }`}
                        title={allStoppedSelected ? "Deselect all stopped" : "Select all stopped"}
                      >
                        {allStoppedSelected && <Check size={10} weight="bold" className="text-white" />}
                      </button>
                    )}
                  </th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">STOP count</th>
                  <th className="text-right px-4 py-3">START count</th>
                  <th className="text-left px-4 py-3">Last stopped</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-neutral-500 text-sm">
                      No rows match "{query}".
                    </td>
                  </tr>
                )}
                {filtered.map((row) => {
                  const isStopped = row.status === "stopped";
                  const isSelected = selectedPhones.has(row.phone);
                  // Surface the most recent bulk-restore note (if any) so
                  // ops can recall WHY a restore fired. Reads the last
                  // entry in `restore_history` that carries a `note`
                  // field (rows without notes are silent).
                  const lastNoteEntry = (row.restore_history || [])
                    .filter((h) => h && h.note)
                    .slice(-1)[0];
                  const lastNote = lastNoteEntry?.note || null;
                  const lastNoteAt = lastNoteEntry?.restored_at || null;
                  const historyLen = (row.restore_history || []).length;
                  const isExpanded = expandedPhones.has(row.phone);
                  return (
                    <React.Fragment key={row.phone}>
                    <tr
                      data-testid={`admin-sms-suppressions-row-${row.phone}`}
                      className={`border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors ${
                        isStopped ? "bg-amber-500/[0.03]" : ""
                      } ${isSelected ? "bg-[#FF5A1F]/[0.08]" : ""}`}
                    >
                      <td className="px-3 py-3">
                        {isStopped && (
                          <button
                            onClick={() => togglePhone(row.phone)}
                            data-testid={`admin-sms-suppressions-check-${row.phone}`}
                            className={`w-4 h-4 rounded border-2 inline-flex items-center justify-center transition-colors ${
                              isSelected
                                ? "bg-[#FF5A1F] border-[#FF5A1F]"
                                : "border-white/30 hover:border-white/60"
                            }`}
                            aria-label={`Select ${row.phone}`}
                          >
                            {isSelected && <Check size={10} weight="bold" className="text-white" />}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-2">
                          {historyLen > 0 ? (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(row.phone)}
                              data-testid={`admin-sms-suppressions-expand-${row.phone}`}
                              aria-expanded={isExpanded}
                              aria-label={
                                isExpanded
                                  ? `Collapse history for ${row.phone}`
                                  : `Expand history for ${row.phone}`
                              }
                              title={`${historyLen} restore${historyLen !== 1 ? "s" : ""} · click to ${isExpanded ? "collapse" : "expand"} history`}
                              className={`w-4 h-4 rounded inline-flex items-center justify-center transition-colors ${
                                isExpanded
                                  ? "bg-sky-500/20 text-sky-300"
                                  : "text-neutral-500 hover:text-white hover:bg-white/[0.04]"
                              }`}
                            >
                              {isExpanded
                                ? <CaretDown size={11} weight="bold" />
                                : <CaretRight size={11} weight="bold" />}
                            </button>
                          ) : (
                            <span className="w-4 h-4 inline-block" aria-hidden="true" />
                          )}
                          <Phone size={12} className="text-neutral-500 shrink-0" />
                          <span className="text-white font-mono">{row.phone}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          data-testid={`admin-sms-suppressions-status-${row.phone}`}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${
                            isStopped
                              ? "bg-amber-500/10 text-amber-300 border-amber-500/40"
                              : "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                          }`}
                        >
                          {isStopped
                            ? <ShieldWarning size={10} weight="fill" />
                            : <ShieldCheck size={10} weight="fill" />}
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-white font-mono tabular-nums">
                        {row.stop_count || 0}
                      </td>
                      <td className="px-4 py-3 text-right text-neutral-400 font-mono tabular-nums">
                        {row.start_count || 0}
                      </td>
                      <td className="px-4 py-3 text-neutral-400 text-xs">
                        <div className="flex flex-col gap-0.5">
                          <span>{fmtDate(row.stopped_at)}</span>
                          {lastNote && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] text-neutral-400 max-w-[220px] cursor-help group"
                              data-testid={`admin-sms-suppressions-restore-note-${row.phone}`}
                              title={`Last restore note (${lastNoteAt ? fmtDate(lastNoteAt) : "unknown"}): "${lastNote}"`}
                            >
                              <ChatCircleText
                                size={11}
                                weight="fill"
                                className="text-sky-400 shrink-0"
                              />
                              <span className="truncate italic group-hover:text-neutral-200 transition-colors">
                                "{lastNote}"
                              </span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isStopped ? (
                          <button
                            onClick={() => restore(row.phone)}
                            disabled={restoringPhone === row.phone}
                            data-testid={`admin-sms-suppressions-restore-${row.phone}`}
                            className="inline-flex items-center gap-1 text-[11px] rounded-full px-3 py-1 bg-[#FF5A1F] hover:bg-[#FF5A1F]/90 disabled:opacity-50 text-white font-semibold transition-colors"
                          >
                            <ArrowUUpLeft size={11} weight="bold" />
                            {restoringPhone === row.phone ? "Restoring…" : "Opt back in"}
                          </button>
                        ) : (
                          <span className="text-neutral-600 text-[10px]">
                            {historyLen
                              ? `restored ×${historyLen}`
                              : "active"}
                          </span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && historyLen > 0 && (
                      <tr
                        data-testid={`admin-sms-suppressions-history-drawer-${row.phone}`}
                        className="border-t border-white/[0.02] bg-white/[0.015]"
                      >
                        <td colSpan="7" className="px-4 py-3">
                          <div className="pl-8 space-y-2">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-2">
                                <ChatCircleText size={11} weight="fill" className="text-sky-400" />
                                Restore history · {historyLen} entr{historyLen === 1 ? "y" : "ies"}
                              </div>
                              {/* Re-stop — only surfaces on currently
                                  active rows. Fires bulk-stop for one
                                  phone so ops can walk back a mistaken
                                  restore without leaving the drawer. */}
                              {!isStopped && (
                                <button
                                  type="button"
                                  onClick={() => restopPhone(row.phone)}
                                  disabled={restoppingPhone === row.phone}
                                  data-testid={`admin-sms-suppressions-restop-${row.phone}`}
                                  className="inline-flex items-center gap-1 text-[10px] rounded-full px-2.5 py-1 border border-amber-500/30 text-amber-300 hover:bg-amber-500/[0.08] hover:border-amber-500/50 disabled:opacity-50 transition-colors"
                                  title={`Walk back the last restore for ${row.phone} — flips the row back to STOPPED`}
                                >
                                  <Prohibit size={10} weight="bold" />
                                  {restoppingPhone === row.phone
                                    ? "Re-stopping…"
                                    : "Re-stop"}
                                </button>
                              )}
                            </div>
                            <ul className="space-y-1.5">
                              {[...(row.restore_history || [])].reverse().map((h, idx) => (
                                <li
                                  key={idx}
                                  data-testid={`admin-sms-suppressions-history-entry-${row.phone}-${historyLen - 1 - idx}`}
                                  className="flex flex-col sm:flex-row sm:items-baseline gap-2 text-xs border-l-2 border-sky-500/30 pl-3 py-1"
                                >
                                  <span className="text-neutral-400 font-mono tabular-nums shrink-0">
                                    {fmtDate(h.restored_at)}
                                  </span>
                                  <span className="text-neutral-500 text-[10px] shrink-0">
                                    by {h.restored_by || "system"}
                                    {h.bulk && (
                                      <span className="ml-1 inline-flex items-center rounded-full px-1.5 py-0.5 bg-sky-500/10 text-sky-300 text-[9px] uppercase tracking-wider font-semibold">
                                        bulk
                                      </span>
                                    )}
                                  </span>
                                  {h.note ? (
                                    <span className="italic text-neutral-200 flex-1 min-w-0">
                                      "{h.note}"
                                    </span>
                                  ) : (
                                    <span className="text-neutral-600 text-[10px] italic">
                                      (no note)
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
